import { createFileRoute, Link, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import {
  useState,
  useRef,
  useEffect,
  type SyntheticEvent,
  type ComponentType,
  type CSSProperties,
} from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import {
  FiArrowLeft,
  FiImage,
  FiEdit2,
  FiTrash2,
  FiChevronRight,
  FiBriefcase,
  FiVideo,
  FiUser,
  FiPlay,
  FiShare2,
  FiX,
  FiChevronLeft,
  FiGlobe,
} from 'react-icons/fi';
import { SiSpotify, SiApplemusic, SiSoundcloud, SiYoutube } from 'react-icons/si';
import { FaAmazon } from 'react-icons/fa6';
import { DeleteMediaModal } from '../components/media/DeleteMediaModal';
import {
  useGetMediaDetail,
  useGetPublicMediaDetail,
  useDeleteMedia,
  isVideoUrl,
} from '../api/media';
import { useGetPublicProfile } from '../api/profile';
import { useAuth } from '../hooks/useAuth';
import { getProfileIdentifier } from '../utils/profileUrl';
import { MEDIA_FALLBACK_IMAGE_SRC, resolveVideoPosterUrl } from '../utils/mediaFallback';
import { Avatar } from '../components/ui/Avatar';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { formatServicePrice } from '../utils/price';

export const Route = createFileRoute('/media/$mediaId')({
  component: MediaDetail,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(fullName?: string, username?: string): string {
  if (fullName?.trim()) {
    return fullName
      .trim()
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return (username?.[0] ?? 'U').toUpperCase();
}

function displayName(fullName?: string, username?: string): string {
  if (fullName?.trim()) return fullName.trim();
  if (username?.trim()) return `@${username.trim()}`;
  return 'User';
}

function timeAgo(isoString?: string): string {
  if (!isoString) return '';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const diffMon = Math.floor(diffDay / 30);
  if (diffMon < 12) return `${diffMon} month${diffMon === 1 ? '' : 's'} ago`;
  const diffYr = Math.floor(diffMon / 12);
  return `${diffYr} year${diffYr === 1 ? '' : 's'} ago`;
}

const PLATFORM_LINKS: Array<{
  key: 'website' | 'spotify' | 'soundcloud' | 'apple_music' | 'amazon_music' | 'youtube_music';
  label: string;
  icon: ComponentType<{ size?: number; className?: string; style?: CSSProperties }>;
  color: string;
}> = [
  { key: 'website', label: 'Website', icon: FiGlobe, color: '#3b82f6' },
  { key: 'spotify', label: 'Spotify', icon: SiSpotify, color: '#1DB954' },
  { key: 'soundcloud', label: 'SoundCloud', icon: SiSoundcloud, color: '#FF5500' },
  { key: 'apple_music', label: 'Apple Music', icon: SiApplemusic, color: '#fc3c44' },
  { key: 'amazon_music', label: 'Amazon Music', icon: FaAmazon, color: '#FF9900' },
  { key: 'youtube_music', label: 'YouTube Music', icon: SiYoutube, color: '#FF0000' },
];

// ─── Component ────────────────────────────────────────────────────────────────

function MediaDetail() {
  const { mediaId } = Route.useParams();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasMainImageError, setHasMainImageError] = useState(false);
  const [hasModalImageError, setHasModalImageError] = useState(false);
  const [videoLoadState, setVideoLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [modalVideoLoadState, setModalVideoLoadState] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [videoRetryKey, setVideoRetryKey] = useState(0);
  const [modalVideoRetryKey, setModalVideoRetryKey] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { user, isAuthenticated } = useAuth();

  // Pause + reset play state whenever the active slide changes
  useEffect(() => {
    setIsVideoPlaying(false);
    setHasMainImageError(false);
    setHasModalImageError(false);
    setVideoLoadState('idle');
    setModalVideoLoadState('idle');
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [currentSlide]);

  // Use authenticated endpoint for logged-in users, public endpoint for guests
  const authMediaResult = useGetMediaDetail(isAuthenticated ? mediaId : '');
  const publicMediaResult = useGetPublicMediaDetail(!isAuthenticated ? mediaId : '');
  const { data: media, isLoading } = isAuthenticated ? authMediaResult : publicMediaResult;
  const creatorUserId = media?.creator?.id ?? media?.user_id ?? (media as any)?.user;
  const { data: creatorProfile } = useGetPublicProfile(creatorUserId);
  const deleteMedia = useDeleteMedia();
  const routerState = useRouterState();
  const mediaUrls = media?.media_urls ?? [];

  useEffect(() => {
    if (!isMediaModalOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMediaModalOpen(false);
      if (event.key === 'ArrowRight') {
        setCurrentSlide((prev) => (prev < mediaUrls.length - 1 ? prev + 1 : prev));
      }
      if (event.key === 'ArrowLeft') {
        setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isMediaModalOpen, mediaUrls.length]);

  // Ownership: only authenticated users who own the media can edit/delete
  const currentUserId = user?.id ?? localStorage.getItem('user_id') ?? '';
  const mediaOwnerId = String((media as any)?.user ?? (media as any)?.user_id ?? '');
  const isOwner =
    isAuthenticated && !!currentUserId && !!mediaOwnerId && mediaOwnerId === String(currentUserId);

  const handleDelete = async () => {
    try {
      await deleteMedia.mutateAsync(Number(mediaId));
      navigate({ to: '/media' });
    } catch {
      // Handled by mutation
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: media?.caption ?? 'Media', url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url).catch(() => {});
    }
  };

  // When on the /edit sub-route, render the edit form instead of the detail view
  if (routerState.location.pathname.endsWith('/edit')) {
    return <Outlet />;
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (isLoading) {
    return <LoadingLogo />;
  }

  if (!media) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-400 text-lg">Media not found.</p>
        <Link to="/" className="text-blue-500 hover:underline text-sm">
          Back to home
        </Link>
      </div>
    );
  }

  const isVideo = media.media_type === 'video';
  const currentUrl = mediaUrls[currentSlide] ?? '';
  const currentSlideIsVideo = !!currentUrl && isVideoUrl(currentUrl);
  const shouldRenderVideo = currentSlideIsVideo || (!currentUrl && isVideo);
  const currentPoster = resolveVideoPosterUrl(mediaUrls, media.video_poster_urls, currentSlide);
  const safeVideoPoster = currentPoster === MEDIA_FALLBACK_IMAGE_SRC ? undefined : currentPoster;

  const handleImageFallback = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.dataset.fallbackApplied === 'true') return;
    img.dataset.fallbackApplied = 'true';
    img.src = MEDIA_FALLBACK_IMAGE_SRC;
  };
  const handleMainImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    setHasMainImageError(true);
    handleImageFallback(event);
  };

  const handleModalImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    setHasModalImageError(true);
    handleImageFallback(event);
  };

  const retryMainVideo = () => {
    setVideoLoadState('loading');
    setVideoRetryKey((prev) => prev + 1);
    setIsVideoPlaying(false);
  };

  const retryModalVideo = () => {
    setModalVideoLoadState('loading');
    setModalVideoRetryKey((prev) => prev + 1);
  };

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setCurrentSlide((p) => Math.min(p + 1, mediaUrls.length - 1));
      else setCurrentSlide((p) => Math.max(p - 1, 0));
    }
    setTouchStart(null);
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Breadcrumb + action buttons ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8 mt-2">
          <div className="flex items-center gap-2 text-zinc-500 text-[13px] sm:text-[14px] overflow-x-auto no-scrollbar whitespace-nowrap">
            <button
              onClick={() => window.history.back()}
              className="hover:text-white transition-colors shrink-0 p-1 -ml-1 rounded-full hover:bg-white/5"
            >
              <FiArrowLeft size={20} />
            </button>
            {shouldRenderVideo ? (
              <FiVideo size={16} className="shrink-0" />
            ) : (
              <FiImage size={16} className="shrink-0" />
            )}
            <Link to="/media" className="hover:text-white transition-colors font-medium shrink-0">
              Media
            </Link>
            <span className="text-zinc-700 shrink-0">/</span>
            <span className="text-zinc-400 shrink-0 truncate min-w-0 max-w-[40vw] sm:max-w-xs">
              {media.title
                ? media.title.slice(0, 40) + (media.title.length > 40 ? '…' : '')
                : media.caption
                  ? media.caption.slice(0, 40) + (media.caption.length > 40 ? '…' : '')
                  : 'Details'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              className="flex-1 sm:flex-initial rounded-full gap-2 px-6 bg-zinc-900 border-white/5 hover:bg-white/5"
              onClick={handleShare}
            >
              <FiShare2 size={16} />
              <span>Share</span>
            </Button>
            {isOwner && (
              <Button
                variant="secondary"
                className="flex-1 sm:flex-initial rounded-full gap-2 px-6 bg-zinc-900 border-white/5 hover:bg-white/5"
                onClick={() => navigate({ to: '/media/$mediaId/edit', params: { mediaId } })}
              >
                <FiEdit2 size={16} />
                <span>Edit</span>
              </Button>
            )}
            {isOwner && (
              <Button
                variant="secondary"
                className="flex-1 sm:flex-initial rounded-full gap-2 px-6 bg-zinc-900 border-white/5 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={deleteMedia.isPending}
              >
                {deleteMedia.isPending ? (
                  <span className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                ) : (
                  <FiTrash2 size={16} />
                )}
                <span>{deleteMedia.isPending ? 'Deleting…' : 'Delete'}</span>
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* ── Left: content ── */}
          <div className="flex-1 flex flex-col gap-6">
            {/* Posted timestamp + title */}
            {media.created_at && (
              <p className="text-zinc-500 text-[13px]">Posted: {timeAgo(media.created_at)}</p>
            )}
            {(media.title || media.caption) && (
              <h1 className="text-white text-2xl sm:text-3xl font-bold leading-snug">
                {media.title || 'Media'}
              </h1>
            )}
            {/* Main viewer */}
            <div
              className="relative group rounded-2xl sm:rounded-3xl overflow-hidden bg-zinc-900 aspect-[4/3] sm:aspect-[16/8] lg:aspect-[16/7]"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {shouldRenderVideo && currentUrl ? (
                <>
                  {videoLoadState === 'error' ? (
                    <div className="w-full h-full bg-black flex flex-col items-center justify-center gap-4 p-6">
                      <img
                        src={currentPoster}
                        alt="Video fallback"
                        className="max-h-[60%] object-contain rounded-xl"
                        onError={handleImageFallback}
                      />
                      <p className="text-zinc-400 text-sm text-center">
                        Video failed to load. You can retry now.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        className="rounded-full px-5 bg-zinc-900 border-white/10 hover:bg-white/5"
                        onClick={retryMainVideo}
                      >
                        Retry Video
                      </Button>
                    </div>
                  ) : (
                    <video
                      ref={videoRef}
                      key={`${currentUrl}-${videoRetryKey}`}
                      src={currentUrl}
                      controls
                      preload="metadata"
                      poster={safeVideoPoster}
                      className="w-full h-full object-contain bg-black"
                      onLoadStart={() => setVideoLoadState('loading')}
                      onLoadedData={() => setVideoLoadState('ready')}
                      onCanPlay={() => setVideoLoadState('ready')}
                      onError={() => {
                        setVideoLoadState('error');
                        setIsVideoPlaying(false);
                      }}
                      onPlay={() => setIsVideoPlaying(true)}
                      onPause={() => setIsVideoPlaying(false)}
                      onEnded={() => setIsVideoPlaying(false)}
                    />
                  )}
                  {(videoLoadState === 'loading' || videoLoadState === 'idle') && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                      <div className="w-8 h-8 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                  {/* Play overlay — hidden once playing */}
                  {!isVideoPlaying && videoLoadState === 'ready' && (
                    <button
                      onClick={() => videoRef.current?.play()}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                    >
                      <div className="w-18 h-18 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                        <FiPlay size={32} className="text-white ml-1" />
                      </div>
                    </button>
                  )}
                </>
              ) : currentUrl ? (
                <img
                  src={currentUrl}
                  alt={media.caption ?? ''}
                  onClick={() => setIsMediaModalOpen(true)}
                  onError={handleMainImageError}
                  className="w-full h-full object-contain bg-black transition-transform duration-500 cursor-pointer"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                  <FiImage size={48} />
                </div>
              )}

              {mediaUrls.length > 1 && (
                <>
                  {/* Left arrow — hidden on first slide */}
                  {currentSlide > 0 && (
                    <button
                      onClick={() => setCurrentSlide((p) => (p > 0 ? p - 1 : p))}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-all"
                    >
                      <FiChevronLeft size={20} />
                    </button>
                  )}
                  {/* Right arrow — hidden on last slide */}
                  {currentSlide < mediaUrls.length - 1 && (
                    <button
                      onClick={() => setCurrentSlide((p) => (p < mediaUrls.length - 1 ? p + 1 : p))}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-all"
                    >
                      <FiChevronRight size={20} />
                    </button>
                  )}
                </>
              )}
              {hasMainImageError && !shouldRenderVideo && (
                <div className="absolute right-4 top-4 bg-black/70 border border-white/10 text-zinc-200 text-xs px-3 py-1.5 rounded-full">
                  Unable to load image.
                </div>
              )}
            </div>

            {/* Thumbnails strip — below the main image */}
            {mediaUrls.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1 no-scrollbar">
                {mediaUrls.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={`w-12 h-12 rounded-xl overflow-hidden border-2 transition-all shrink-0 ${
                      i === currentSlide
                        ? 'border-white/80 scale-105 ring-1 ring-white/20'
                        : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                  >
                    {isVideoUrl(url) ? (
                      <img
                        src={resolveVideoPosterUrl(mediaUrls, media.video_poster_urls, i)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={handleImageFallback}
                      />
                    ) : (
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={handleImageFallback}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Caption body text — rendered as flowing prose */}
            {media.caption && (
              <div className="flex flex-col gap-4 mt-2">
                {media.caption
                  .split('\n\n')
                  .filter(Boolean)
                  .map((para, i) => (
                    <p key={i} className="text-zinc-300 text-[15px] leading-relaxed">
                      {para}
                    </p>
                  ))}
              </div>
            )}
          </div>

          {/* ── Right: sidebar ── */}
          <div className="w-full lg:w-80 flex flex-col gap-8">
            {/* Posted by */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-0.75 h-4 bg-blue-500" />
                <h3 className="text-white text-[15px] font-semibold">Posted by</h3>
              </div>
              <Link
                to="/profile/$identifier"
                params={{
                  identifier: getProfileIdentifier({
                    id: creatorUserId,
                    username: creatorProfile?.username,
                  }),
                }}
                className="flex items-center gap-3 bg-zinc-900/40 border border-white/5 rounded-2xl p-3 hover:border-white/15 transition-colors"
              >
                <Avatar
                  src={
                    media.creator?.profile_image ??
                    creatorProfile?.prof_pics ??
                    creatorProfile?.profile_image
                  }
                  alt={media.creator?.full_name || creatorProfile?.full_name || 'User'}
                  name={
                    media.creator?.full_name ??
                    creatorProfile?.full_name ??
                    media.creator?.username ??
                    creatorProfile?.username ??
                    media.username
                  }
                  size="md"
                  className="shrink-0"
                />
                <span className="text-white text-[14px] font-medium">
                  {media.creator?.full_name ??
                    creatorProfile?.full_name ??
                    media.creator?.username ??
                    creatorProfile?.username ??
                    media.username ??
                    'User'}
                </span>
              </Link>
            </div>

            {/* Collaborators */}
            {(media.collaborators?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-0.75 h-4 bg-blue-500" />
                  <h3 className="text-white text-[15px] font-semibold">Collaborators</h3>
                </div>
                <div className="flex flex-wrap gap-3">
                  {media.collaborators!.map((c: any) => {
                    const name = displayName(c.full_name, c.username);
                    const initials = getInitials(c.full_name, c.username);
                    const primaryType = c.user_types?.[0];
                    const profileId = getProfileIdentifier({ id: c.id, username: c.username });
                    return (
                      <Link
                        key={c.id}
                        to="/profile/$identifier"
                        params={{ identifier: profileId }}
                        className="flex items-center gap-2 bg-zinc-900/60 border border-white/5 rounded-full pl-1.5 pr-4 py-1.5 hover:border-white/20 hover:bg-white/5 transition-all"
                      >
                        {/* Avatar or initials */}
                        <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-[11px] font-bold text-zinc-300 shrink-0">
                          {initials}
                        </div>
                        <span className="text-white text-[14px] font-medium">{name}</span>
                        {primaryType && (
                          <span className="text-blue-500 text-[12px] font-medium bg-blue-500/10 px-2 py-0.5 rounded-full">
                            {primaryType}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Event */}
            {media.event && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-0.75 h-4 bg-blue-500" />
                  <h3 className="text-white text-[15px] font-semibold">Event</h3>
                </div>
                <Link
                  to="/events/$eventId"
                  params={{ eventId: String(media.event.id) }}
                  search={{ invite_token: undefined }}
                  className="bg-zinc-900/40 border border-white/10 rounded-3xl p-5 flex gap-4 items-center hover:border-blue-500/40 hover:bg-zinc-900/60 transition-all group"
                >
                  <div className="w-14 h-14 rounded-2xl overflow-hidden shrink-0">
                    {media.event.featured_image?.file_url ? (
                      <img
                        src={media.event.featured_image.file_url}
                        alt={media.event.event_title ?? 'Event'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-2xl bg-zinc-800 group-hover:bg-blue-500/10 flex items-center justify-center text-zinc-500 group-hover:text-blue-400 shrink-0 transition-colors">
                        <FiImage size={22} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <h4 className="text-blue-500 font-semibold text-[16px] truncate">
                      {media.event.event_title ?? media.event.name}
                    </h4>
                    {media.event.description && (
                      <p className="text-zinc-500 text-[13px] truncate">
                        {media.event.description}
                      </p>
                    )}
                  </div>
                </Link>
              </div>
            )}

            {/* Service */}
            {media.service && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-0.75 h-4 bg-blue-500" />
                  <h3 className="text-white text-[15px] font-semibold">Services</h3>
                </div>
                <Link
                  to="/services/$serviceId"
                  params={{ serviceId: String(media.service.id) }}
                  className="flex flex-col gap-4 bg-zinc-900/40 border border-white/10 rounded-3xl p-5 relative hover:border-blue-500/40 hover:bg-zinc-900/60 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-zinc-800 group-hover:bg-blue-500/10 flex items-center justify-center text-zinc-500 group-hover:text-blue-400 shrink-0 transition-colors">
                      <FiBriefcase size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-blue-500 font-semibold text-lg truncate">
                        {media.service.name}
                      </h4>
                      {media.service.price && (
                        <p className="text-zinc-500 text-[13px] italic">
                          {formatServicePrice(media.service.price)}
                        </p>
                      )}
                    </div>
                    <FiChevronRight
                      size={18}
                      className="text-zinc-600 group-hover:text-blue-400 shrink-0 transition-colors"
                    />
                  </div>
                  {media.service.description && (
                    <p className="text-zinc-400 text-[15px] italic leading-relaxed">
                      "{media.service.description}"
                    </p>
                  )}
                </Link>
              </div>
            )}

            {/* Platform Links */}
            {PLATFORM_LINKS.some((p) => media[p.key]) && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-0.75 h-4 bg-blue-500" />
                  <h3 className="text-white text-[15px] font-semibold">Platform Links</h3>
                </div>
                <div className="flex flex-wrap gap-3">
                  {PLATFORM_LINKS.filter((p) => media[p.key]).map((p) => (
                    <a
                      key={p.key}
                      href={media[p.key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-zinc-900/60 border border-white/5 rounded-full pl-3 pr-4 py-2 hover:border-white/20 hover:bg-white/5 transition-all"
                    >
                      <p.icon size={15} style={{ color: p.color }} className="shrink-0" />
                      <span className="text-white text-[13px] font-medium">{p.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Empty sidebar state — only for optional items */}
            {!media.event &&
              !media.collaborators?.length &&
              !media.service &&
              !PLATFORM_LINKS.some((p) => media[p.key]) && (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-zinc-600 text-center">
                  <FiUser size={28} />
                  <p className="text-[13px]">No collaborators, event, or service attached.</p>
                </div>
              )}
          </div>
        </div>
      </main>

      <footer className="mt-20 border-t border-white/5 py-10 px-8 text-zinc-600 text-[14px] text-center lg:text-left">
        © 2026 Musicade
      </footer>

      <BottomNav />

      {isDeleteModalOpen && (
        <DeleteMediaModal
          isLoading={deleteMedia.isPending}
          onConfirm={handleDelete}
          onCancel={() => {
            if (!deleteMedia.isPending) setIsDeleteModalOpen(false);
          }}
        />
      )}

      {/* Media Fullscreen Modal */}
      {isMediaModalOpen && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center bg-black/95 backdrop-blur-md cursor-pointer"
          onClick={() => setIsMediaModalOpen(false)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMediaModalOpen(false);
            }}
            className="absolute top-6 right-6 p-3 bg-black/70 hover:bg-black/90 border border-white/20 rounded-full text-white transition-colors z-50"
          >
            <FiX size={24} />
          </button>

          <div
            className="w-full h-full max-w-6xl max-h-screen flex flex-col items-center justify-center relative cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {shouldRenderVideo ? (
              modalVideoLoadState === 'error' ? (
                <div className="max-w-full max-h-[90vh] w-full h-full flex flex-col items-center justify-center gap-4">
                  <img
                    src={currentPoster}
                    alt="Video fallback"
                    className="max-w-full max-h-[70vh] object-contain rounded-2xl"
                    onError={handleImageFallback}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    className="rounded-full px-5 bg-zinc-900 border-white/10 hover:bg-white/5"
                    onClick={retryModalVideo}
                  >
                    Retry Video
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <video
                    key={`${currentUrl}-${modalVideoRetryKey}`}
                    src={currentUrl}
                    controls
                    autoPlay
                    preload="metadata"
                    poster={safeVideoPoster}
                    className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
                    onLoadStart={() => setModalVideoLoadState('loading')}
                    onLoadedData={() => setModalVideoLoadState('ready')}
                    onCanPlay={() => setModalVideoLoadState('ready')}
                    onError={() => setModalVideoLoadState('error')}
                  />
                  {(modalVideoLoadState === 'loading' || modalVideoLoadState === 'idle') && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/25 rounded-2xl pointer-events-none">
                      <div className="w-8 h-8 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              )
            ) : (
              <img
                src={currentUrl}
                alt={media?.caption ?? ''}
                className="max-w-[88vw] max-h-[75vh] object-contain rounded-2xl shadow-2xl"
                onError={handleModalImageError}
              />
            )}

            {hasModalImageError && !shouldRenderVideo && (
              <div className="mt-4 bg-black/70 border border-white/10 text-zinc-200 text-xs px-3 py-1.5 rounded-full">
                Unable to load image.
              </div>
            )}

            {mediaUrls.length > 0 && (
              <div className="mt-4 bg-black/60 backdrop-blur-sm text-white text-sm font-medium px-3 py-1.5 rounded-full z-10 select-none">
                {currentSlide + 1} / {mediaUrls.length}
              </div>
            )}

            {mediaUrls.length > 1 && (
              <>
                {/* Previous arrow — hidden on first slide */}
                {currentSlide > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
                    }}
                    className="absolute left-6 top-1/2 -translate-y-1/2 p-4 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                  >
                    <FiChevronLeft size={32} />
                  </button>
                )}
                {/* Next arrow — hidden on last slide */}
                {currentSlide < mediaUrls.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentSlide((prev) => (prev < mediaUrls.length - 1 ? prev + 1 : prev));
                    }}
                    className="absolute right-6 top-1/2 -translate-y-1/2 p-4 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                  >
                    <FiChevronRight size={32} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
